import { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { gerarBalancete } from "../utils/mockData";
import { fmtBRL } from "../utils/formatters";
import { ExportDropdown } from "../components/ui/ExportDropdown";

export function FinanceiroDreContabilPage() {
  const balancete = useMemo(() => gerarBalancete(), []);

  const totalInicial = balancete.reduce((s, r) => s + r.saldoInicial, 0);
  const totalDebito = balancete.reduce((s, r) => s + r.debito, 0);
  const totalCredito = balancete.reduce((s, r) => s + r.credito, 0);
  const totalFinal = balancete.reduce((s, r) => s + r.saldoFinal, 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-black text-white font-sans">
          DRE Contábil — Balancete por conta
        </h1>
        <p className="text-white/50 text-sm mt-1">
          Balancete detalhado por conta contábil (plano de contas brasileiro).
        </p>
      </div>

      <div className="mb-6 rounded-xl border border-indra-turquesa/30 bg-indra-turquesa-muted p-4 flex items-start gap-3">
        <AlertCircle className="text-indra-turquesa flex-shrink-0 mt-0.5" size={18} />
        <div className="text-sm">
          <strong className="text-indra-turquesa">Preview.</strong>{" "}
          <span className="text-white/80">
            Em breve — depende da importação do balancete contábil do Omie (#127). O
            DRE <em>Gerencial</em> (aba ao lado) já consome dados reais.
          </span>
        </div>
      </div>

      <div className="bg-indra-azul-surface rounded-xl border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="text-white font-semibold text-sm">Balancete de verificação</h3>
          <ExportDropdown
            filename="cortex-dre-contabil-balancete"
            columns={[
              { key: "conta", header: "Conta" },
              { key: "saldoInicial", header: "Saldo inicial (R$)", format: (r) => fmtBRL(r.saldoInicial as number) },
              { key: "debito", header: "Débito (R$)", format: (r) => fmtBRL(r.debito as number) },
              { key: "credito", header: "Crédito (R$)", format: (r) => fmtBRL(r.credito as number) },
              { key: "saldoFinal", header: "Saldo final (R$)", format: (r) => fmtBRL(r.saldoFinal as number) },
            ]}
            rows={balancete as unknown as Array<Record<string, unknown>>}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-white/60 font-semibold">
                  Conta
                </th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wider text-white/60 font-semibold font-mono">
                  Saldo inicial
                </th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wider text-white/60 font-semibold font-mono">
                  Débito
                </th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wider text-white/60 font-semibold font-mono">
                  Crédito
                </th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wider text-white/60 font-semibold font-mono">
                  Saldo final
                </th>
              </tr>
            </thead>
            <tbody>
              {balancete.map((r, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-5 py-3 text-sm text-white">{r.conta}</td>
                  <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-white/80">
                    {fmtBRL(r.saldoInicial, { decimals: 0 })}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-indra-positivo/80">
                    {fmtBRL(r.debito, { decimals: 0 })}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-indra-negativo/80">
                    {fmtBRL(r.credito, { decimals: 0 })}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-white font-semibold">
                    {fmtBRL(r.saldoFinal, { decimals: 0 })}
                  </td>
                </tr>
              ))}
              <tr className="bg-indra-azul-deep font-bold">
                <td className="px-5 py-3 text-sm text-indra-turquesa uppercase tracking-wider">
                  Totais
                </td>
                <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-white">
                  {fmtBRL(totalInicial, { decimals: 0 })}
                </td>
                <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-indra-positivo">
                  {fmtBRL(totalDebito, { decimals: 0 })}
                </td>
                <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-indra-negativo">
                  {fmtBRL(totalCredito, { decimals: 0 })}
                </td>
                <td className="px-5 py-3 text-right text-sm font-mono tabular-nums text-indra-turquesa">
                  {fmtBRL(totalFinal, { decimals: 0 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-white/40 mt-6 leading-relaxed max-w-3xl">
        Balancete de verificação — saldo final = saldo inicial + débitos − créditos
        para contas de ativo e despesa; invertido para passivo e receita.
      </p>
    </div>
  );
}
